import type {
  ClassifiedStop,
  ClusterMetadata,
  MLTripSummary,
  PlaceMetadata,
} from "@/features/towers/types";

function displayNameFromRepresentativeAndCount(
  meta: ClusterMetadata,
): string | null {
  const rep = meta.representativeName;
  if (rep === undefined || rep === null || rep === "") return null;
  const count = meta.placeCount ?? 0;
  if (count <= 1) return rep;
  return `${rep} + ${count - 1}`;
}

function getClusterDisplayPartsFromClusterName(
  clusterName: string | undefined | null,
): ClusterDisplayPartsFromMetadata | undefined {
  if (clusterName === undefined || clusterName === null || clusterName === "") {
    return undefined;
  }

  const match = /^(.*)\s\+\s(\d+)$/.exec(clusterName);
  if (!match) {
    return {
      representativeName: clusterName,
      otherCount: 0,
    };
  }

  return {
    representativeName: match[1],
    otherCount: Number(match[2]),
  };
}

function assignClusterNameToMetadata(
  map: Record<string, ClusterMetadata>,
  clusterId: string | undefined,
  clusterName: string | undefined | null,
): void {
  if (clusterId == null || clusterId === "") {
    return;
  }
  const trimmed = clusterName?.trim();
  if (!trimmed) {
    return;
  }
  const existing = map[clusterId]?.clusterName?.trim();
  if (existing) {
    return;
  }
  map[clusterId] = { ...map[clusterId], clusterName: trimmed };
}

function mergeClusterMetadataRecord(
  target: Record<string, ClusterMetadata>,
  source: Record<string, ClusterMetadata> | undefined,
): void {
  if (!source) {
    return;
  }
  for (const [clusterId, meta] of Object.entries(source)) {
    const existing = target[clusterId] ?? {};
    target[clusterId] = {
      ...existing,
      ...meta,
      clusterName:
        existing.clusterName?.trim() || meta.clusterName?.trim()
          ? existing.clusterName?.trim() || meta.clusterName?.trim()
          : undefined,
      representativeName:
        existing.representativeName?.trim() || meta.representativeName?.trim()
          ? existing.representativeName?.trim() ||
            meta.representativeName?.trim()
          : undefined,
    };
  }
}

/** Cluster metadata merged from trip summaries (stops, endpoints, optional trip maps). */
export function buildClusterMetadataFromTripSummaries(
  trips: MLTripSummary[],
): Record<string, ClusterMetadata> {
  const map: Record<string, ClusterMetadata> = {};
  for (const trip of trips) {
    mergeClusterMetadataRecord(map, trip.clusterMetadata);
    assignClusterNameToMetadata(
      map,
      trip.originClusterId,
      trip.originClusterName,
    );
    assignClusterNameToMetadata(
      map,
      trip.destinationClusterId,
      trip.destinationClusterName,
    );
    assignClusterNameToMetadata(
      map,
      trip.predictedNextStopClusterId,
      trip.predictedNextStopClusterName,
    );
    for (const stop of trip.stopSummaries) {
      assignClusterNameToMetadata(map, stop.clusterId, stop.clusterName);
    }
  }
  return map;
}

/** Prefer a non-empty clusterName over a raw clusterId when aggregating stops. */
export function mergeTripStopClusterLabel(
  clusterId: string,
  current: string | undefined,
  clusterName: string | undefined | null,
): string {
  const fromStop = clusterName?.trim();
  const candidate = fromStop && fromStop.length > 0 ? fromStop : clusterId;
  if (!current) {
    return candidate;
  }
  if (current === clusterId && candidate !== clusterId) {
    return candidate;
  }
  if (current !== clusterId) {
    return current;
  }
  return candidate;
}

/**
 * Returns cluster display name with fallbacks:
 * clusterName → representativeName + placeCount → "Cluster <clusterId>".
 */
export function getClusterDisplayName(
  clusterId: string,
  clusterMetadata: Record<string, ClusterMetadata>,
): string {
  const meta = clusterMetadata[clusterId];
  const fromMeta =
    meta?.clusterName !== undefined &&
    meta?.clusterName !== null &&
    meta?.clusterName !== ""
      ? meta.clusterName
      : displayNameFromRepresentativeAndCount(meta ?? {});
  return fromMeta ?? `Cluster ${clusterId}`;
}

/** Resolve stop-derived label with cluster metadata when the label is still the id. */
export function resolveClusterDisplayLabel(
  clusterId: string,
  labelFromStops: string,
  clusterMetadata: Record<string, ClusterMetadata>,
): string {
  if (labelFromStops !== clusterId) {
    return labelFromStops;
  }
  return getClusterDisplayName(clusterId, clusterMetadata);
}

export function buildClusterDisplayNameById(
  trips: MLTripSummary[],
  additionalClusterMetadata?: Record<string, ClusterMetadata>,
): Map<string, string> {
  const clusterMetadata = buildClusterMetadataFromTripSummaries(trips);
  if (additionalClusterMetadata) {
    mergeClusterMetadataRecord(clusterMetadata, additionalClusterMetadata);
  }

  const clusterIds = new Set<string>();
  const rawLabelById = new Map<string, string>();

  for (const trip of trips) {
    for (const stop of trip.stopSummaries) {
      const id = stop.clusterId;
      clusterIds.add(id);
      rawLabelById.set(
        id,
        mergeTripStopClusterLabel(id, rawLabelById.get(id), stop.clusterName),
      );
    }
  }

  for (const id of Object.keys(clusterMetadata)) {
    clusterIds.add(id);
  }

  const displayNameById = new Map<string, string>();
  for (const clusterId of clusterIds) {
    const raw = rawLabelById.get(clusterId) ?? clusterId;
    displayNameById.set(
      clusterId,
      resolveClusterDisplayLabel(clusterId, raw, clusterMetadata),
    );
  }

  return displayNameById;
}

/** Display name for a site even when it is excluded from lane-scored stop metrics. */
export function resolveSiteDisplayNameFromTrips(
  trips: MLTripSummary[],
  clusterId: string,
  additionalClusterMetadata?: Record<string, ClusterMetadata>,
): string {
  return (
    buildClusterDisplayNameById(trips, additionalClusterMetadata).get(
      clusterId,
    ) ?? clusterId
  );
}

export function resolveFocusedStopDisplayName(
  tripSummaries: MLTripSummary[],
  focusedClusterId: string | null,
  lateStops: { clusterId: string; label: string }[],
): string {
  if (!focusedClusterId) {
    return "";
  }
  const fromLateStops = lateStops.find((s) => s.clusterId === focusedClusterId)
    ?.label;
  if (fromLateStops && fromLateStops !== focusedClusterId) {
    return fromLateStops;
  }
  return resolveSiteDisplayNameFromTrips(tripSummaries, focusedClusterId);
}

type ClusterDisplayPartsFromMetadata = {
  representativeName: string;
  otherCount: number;
};

/**
 * Returns representative name and other count for a cluster (title + badge).
 * Uses cluster metadata when available; falls back to full display name and 0.
 */
export function getClusterDisplayPartsForCluster(
  clusterId: string,
  clusterMetadata: Record<string, ClusterMetadata>,
): { representativeName: string; otherCount: number } {
  const metaParts = getClusterDisplayPartsFromMetadata(
    clusterMetadata[clusterId],
  );
  if (metaParts) {
    return {
      representativeName: metaParts.representativeName,
      otherCount: metaParts.otherCount,
    };
  }
  return {
    representativeName: getClusterDisplayName(clusterId, clusterMetadata),
    otherCount: 0,
  };
}

export function getClusterDisplayParts(
  stop: ClassifiedStop,
  clusterMetadata: Record<string, ClusterMetadata>,
): { representativeName: string; otherCount: number } {
  return getClusterDisplayPartsForCluster(stop.clusterId, clusterMetadata);
}

/**
 * Returns display parts from cluster metadata (representativeName, placeCount).
 * Falls back to undefined when metadata has no name.
 */
function getClusterDisplayPartsFromMetadata(
  meta: ClusterMetadata | undefined,
): ClusterDisplayPartsFromMetadata | undefined {
  const rep = meta?.representativeName;
  if (rep !== undefined && rep !== null && rep !== "") {
    const count = meta?.placeCount ?? 0;
    const otherCount = Math.max(0, count - 1);
    return { representativeName: rep, otherCount };
  }

  return getClusterDisplayPartsFromClusterName(meta?.clusterName);
}

export type GetPlaceDisplayNameOptions = {
  /** Place name from the stop/place object (e.g. PlaceDeliveryAlignment.placeName). */
  placeNameFromObject?: string | null;
  /** Final fallback when no name is found. Defaults to "Place <placeId>". */
  fallback?: string;
};

/**
 * Returns a display name for a place using metadata with fallbacks:
 * placeMetadata[placeId].placeName → placeNameFromObject → fallback → "Place <placeId>".
 */
export function getPlaceDisplayName(
  placeId: string,
  placeMetadata: Record<string, PlaceMetadata>,
  options: GetPlaceDisplayNameOptions = {},
): string {
  const { placeNameFromObject, fallback } = options;
  const fromMeta = placeMetadata[placeId]?.placeName;
  const name =
    fromMeta !== undefined && fromMeta !== null && fromMeta !== ""
      ? fromMeta
      : placeNameFromObject !== undefined &&
          placeNameFromObject !== null &&
          placeNameFromObject !== ""
        ? placeNameFromObject
        : null;
  return name ?? fallback ?? `Place ${placeId}`;
}

/**
 * Returns customer ID for a place: placeMetadata[placeId].customerId → customerIdFromObject.
 */
export function getPlaceCustomerId(
  placeId: string,
  placeMetadata: Record<string, PlaceMetadata>,
  customerIdFromObject?: string | null,
): string | undefined {
  return (
    placeMetadata[placeId]?.customerId ?? customerIdFromObject ?? undefined
  );
}
